import React from "react";
import ProgramTemplate from "@/components/programs/ProgramTemplate";

const OrganizationalPartnerships = () => (
  <ProgramTemplate
    pageKey="organizational-partnerships"
    breadcrumbLabel="Organizational Partnerships"
    overline="Program 04 · Organizational Partnerships"
    headline="Adaptive Physiology,"
    headlineAccent="inside existing care."
    audience="For treatment centers, clinics, and health organizations"
    lead="Organizational Partnerships integrate Adaptive Physiology™ inside programs that already exist — treatment centers, rehabilitation clinics, mental health organizations, community health programs, and athletic organizations — as an added physiological layer, not a replacement."
    loopHighlight="state"
    whyExists={{
      title: "The nervous system is present in every setting. The framework should be, too.",
      paragraphs: [
        "Wherever behavior change is being attempted — recovery programming, rehabilitation, mental health, community care, athletics — the same underlying physiology is at work. Yet in most settings it is addressed indirectly at best.",
        "Organizational Partnerships bring Adaptive Physiology™ into those settings as an integrative layer, complementing the clinical, therapeutic, or performance work already being done — without asking the organization to abandon what already works.",
      ],
    }}
    howApplied={{
      title: "The loop, adapted to your organization’s workflow.",
      intro:
        "Partnership implementation is designed around the organization’s existing model of care — not around a rigid template.",
      steps: [
        {
          title: "Organizational assessment",
          body: "Review of populations served, current care model, staffing, and where the loop most naturally augments existing practice.",
        },
        {
          title: "Integration design",
          body: "Adaptive Physiology™ protocols are shaped to fit the organization’s workflow — assessments, prescription templates, and reinforcement rhythm calibrated for the setting.",
        },
        {
          title: "Staff enablement",
          body: "Clinical, coaching, and program staff are trained in the framework so implementation is durable and doesn’t depend on any one person.",
        },
        {
          title: "Reassessment & scaling",
          body: "Signal is reviewed at organizational and cohort level; the integration adapts as the organization’s populations and outcomes evolve.",
        },
      ],
    }}
    experience={{
      title: "A physiological layer added to the care you already deliver.",
      intro:
        "Partners describe the difference not as new programming, but as more coherent programming — the same clinical intent, with the underlying physiology finally being addressed.",
      capacities: [
        { label: "Integrative fit", body: "The methodology sitting cleanly inside existing care, not competing with it." },
        { label: "Staff capability", body: "Trained personnel who can deliver the framework across cohorts." },
        { label: "Assessment infrastructure", body: "Repeatable measurement of the markers that actually track adaptation." },
        { label: "Programmatic consistency", body: "Reliable delivery of the loop across shifts, sites, and populations." },
        { label: "Adaptive protocols", body: "Templates that adjust to different populations without losing rigor." },
        { label: "Collaborative research posture", body: "The option to formalize outcomes as data, when the partner is ready." },
      ],
    }}
    designedFor={{
      title: "Institutions committed to integrative care.",
      groups: [
        { title: "Addiction treatment centers", body: "Adding a physiological retraining layer beneath clinical and behavioral work." },
        { title: "Rehabilitation clinics", body: "Extending rehab beyond tissue and biomechanics to autonomic recovery." },
        { title: "Mental health organizations", body: "Complementing therapeutic care with a state-level physiological pathway." },
        { title: "Community health programs", body: "Bringing structured physiology into populations underserved by traditional wellness." },
        { title: "Athletic organizations", body: "Systematic autonomic and adaptive work across teams and rosters." },
        { title: "Research collaborators", body: "Institutions interested in formalizing outcomes and methodology data together." },
      ],
    }}
    nextSteps={{
      primary: {
        label: "Explore a partnership conversation",
        desc: "Introductory conversations are scoped to your organization’s current model.",
        to: "/apply",
      },
      secondary: {
        label: "Consider Professional Consultation",
        desc: "For an individual clinician or coach rather than an organization.",
        to: "/programs/professional-consultation",
      },
    }}
  />
);
export default OrganizationalPartnerships;
